/**
 * ReferenceShell — domain frame for Reference Data.
 *
 * Lookup tables and code catalogs that drive bank workflows. Things in
 * here are domain-stable (NAICS codes, CD penalty rules, IRA distribution
 * reasons) — rarely-changing reference data that the bank relies on but
 * doesn't author on a daily basis.
 *
 * v0.9.8 ships with CD Penalty Rules only. NAICS / IRA Reason Codes /
 * IRA Contribution Limits / Core Director enums plug into the same shell
 * as their editors land.
 */

import { useAuth } from '@/contexts/AuthContext';
import { DomainShell } from '@/components/DomainShell';

const REQ = ['SuperAdmin', 'Admin', 'SysOpsManager'] as const;

const SUBNAV = [
  { slug: 'cd-penalty-rules', label: 'CD Penalty Rules', requiredRoles: REQ },
  { slug: 'ln-verification-rules', label: 'LN Verification Rules', requiredRoles: REQ },
  { slug: 'core-director-codes', label: 'Core Director Codes', requiredRoles: REQ },
  { slug: 'review-aggregate-chips', label: 'Review Aggregate Chips', requiredRoles: REQ },
  { slug: 'home-dashboard-cards', label: 'Home Dashboard Cards', requiredRoles: REQ },
  { slug: 'naics-codes', label: 'NAICS Codes', requiredRoles: REQ },
  { slug: 'state-gov-search-links', label: 'State Gov Search Links', requiredRoles: REQ },
  // ParmVal Sync: backend gates all routes at SuperAdmin — match it in the nav.
  { slug: 'parmval-sync', label: 'ParmVal Sync', requiredRoles: ['SuperAdmin'] as const },
  { slug: 'ira-reason-codes', label: 'IRA Reason Codes', requiredRoles: REQ },
  { slug: 'ira-transaction-codes', label: 'IRA Transaction Codes', requiredRoles: REQ },
  { slug: 'ira-contribution-limits', label: 'IRA Contribution Limits', requiredRoles: REQ },
  { slug: 'ira-institution-relationships', label: 'IRA Institution Relationships', requiredRoles: REQ },
  { slug: 'ira-contribution-phaseouts', label: 'IRA Contribution Phaseouts', requiredRoles: REQ },
];

export function ReferenceShell(): JSX.Element {
  const { user } = useAuth();
  return (
    <DomainShell
      title="Reference Data"
      description="Bank-stable lookup catalogs. NAICS codes, CD early-withdrawal penalty schedules, IRA distribution-reason codes, Core Director enums. Read by every account-opening flow; changes here ripple through the platform."
      basePath="/retail/reference"
      items={SUBNAV.map((s) => ({
        slug: s.slug,
        label: s.label,
        requiredRoles: s.requiredRoles,
      }))}
      userRoles={user?.roles ?? []}
    />
  );
}
